"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.conditionString = conditionString;
exports.isOwnedBy = isOwnedBy;
exports.canUseItem = canUseItem;
exports.descTextFor = descTextFor;
exports.foodTextFor = foodTextFor;
exports.gemTextFor = gemTextFor;
exports.trinketExpMax = trinketExpMax;
exports.trinketGoldCost = trinketGoldCost;
exports.trinketCanLevelUp = trinketCanLevelUp;
const interfaces_1 = require("@lotr/interfaces");
const formatStatForDisplay = (stat, statValue) => {
    const sign = statValue > 0 ? '+' : '';
    const isPercent = stat.includes('Percent');
    const displayValue = isPercent ? `${statValue}%` : statValue;
    const statName = stat.replace('Percent', '');
    return `${sign}${displayValue} ${statName.toUpperCase()}`;
};
function getProp(item, itemDef, prop) {
    return item.mods[prop] || itemDef[prop];
}
function conditionString(item) {
    const condition = item.mods.condition ?? 20000;
    if (condition <= 0)
        return 'broken';
    if (condition <= 2500)
        return 'rough';
    if (condition <= 5000)
        return 'tattered';
    if (condition <= 10000)
        return 'below average';
    if (condition <= 20000)
        return 'average';
    if (condition <= 30000)
        return 'above average';
    if (condition <= 40000)
        return 'mint';
    if (condition <= 50000)
        return 'above mint';
    if (condition <= 99999)
        return 'perfect';
    return 'heavenly';
}
function usesString(item, itemDef) {
    const effect = getProp(item, itemDef, 'useEffect');
    if (!effect || !effect.uses || effect.uses < 0)
        return '';
    const uses = effect.uses;
    if (uses < 3)
        return 'looks brittle';
    if (uses < 9)
        return 'looks cracked';
    if (uses < 20)
        return 'looks normal';
    if (uses < 50)
        return 'surges with energy';
    if (uses < 100)
        return 'crackles with power';
    return 'is flawlessly vibrant';
    // the item <x>
}
function isOwnedBy(player, item) {
    return !item.mods || !item.mods.owner || item.mods.owner === player.username;
}
function canUseItem(player, item, itemDef) {
    const itemClass = getProp(item, itemDef, 'itemClass');
    const useEffect = getProp(item, itemDef, 'useEffect');
    const ounces = getProp(item, itemDef, 'ounces');
    const succorInfo = getProp(item, itemDef, 'succorInfo');
    const condition = item.mods.condition ?? 20000;
    // can't use broken items, traps, or items you don't own
    if (condition <= 0)
        return false;
    if (itemClass === interfaces_1.ItemClass.Trap)
        return false;
    if (!isOwnedBy(player, item))
        return false;
    if (item.name.includes('Rune Scroll') || item.name.includes('Recipe Book')) {
        return true;
    }
    if (itemClass === interfaces_1.ItemClass.Box)
        return true;
    if (itemClass === interfaces_1.ItemClass.Book)
        return true;
    if (useEffect && useEffect.uses)
        return true;
    if (succorInfo)
        return true;
    if (ounces > 0 && itemClass !== interfaces_1.ItemClass.Rock)
        return true;
    return false;
}
function descTextFor(player, item, itemDef, encrustDef, upgradeDefs = [], castIdentifyTier = 0, thiefTier = 0) {
    if (!item || !itemDef) {
        return 'This description cannot be generated at this time.';
    }
    const quality = getProp(item, itemDef, 'quality') ?? 0;
    const value = getProp(item, itemDef, 'value');
    const extendedDesc = getProp(item, itemDef, 'extendedDesc');
    const maxUpgrades = getProp(item, itemDef, 'maxUpgrades');
    const upgrades = getProp(item, itemDef, 'upgrades');
    const isOffhand = getProp(item, itemDef, 'offhand');
    const useEffect = getProp(item, itemDef, 'useEffect');
    const strikeEffect = getProp(item, itemDef, 'strikeEffect');
    const tier = getProp(item, itemDef, 'tier');
    const trait = getProp(item, itemDef, 'trait');
    const requirements = getProp(item, itemDef, 'requirements');
    const pages = getProp(item, itemDef, 'bookPages');
    const usesText = usesString(item, itemDef);
    // calculating stats is more than getting a prop unfortunately
    const baseStats = itemDef.stats ?? {};
    const modStats = item.mods?.stats ?? {};
    const stats = {};
    for (const stat of Object.keys(baseStats)) {
        const statKey = stat;
        const baseStat = baseStats[statKey] ?? 0;
        stats[statKey] ??= 0;
        stats[statKey] += baseStat;
    }
    for (const stat of Object.keys(modStats)) {
        const statKey = stat;
        const baseStat = modStats[statKey] ?? 0;
        stats[statKey] ??= 0;
        stats[statKey] += baseStat;
    }
    const allStats = [
        interfaces_1.Stat.STR,
        interfaces_1.Stat.DEX,
        interfaces_1.Stat.AGI,
        interfaces_1.Stat.WIS,
        interfaces_1.Stat.INT,
        interfaces_1.Stat.WIL,
        interfaces_1.Stat.CHA,
        interfaces_1.Stat.CON,
        interfaces_1.Stat.LUK,
        interfaces_1.Stat.HP,
        interfaces_1.Stat.MP,
    ];
    const affectsAttributes = allStats.some((x) => stats?.[x]);
    const affectedStats = Object.values(interfaces_1.Stat).filter((x) => stats?.[x]);
    const allTexts = [];
    const identifyTier = Math.max(castIdentifyTier, item.mods.identifyTier ?? 0);
    const itemClass = getProp(item, itemDef, 'itemClass');
    // get the number of stars before the desc
    if (quality - 2 > 0) {
        allTexts.push(Array(quality - 2)
            .fill('★')
            .join(''));
    }
    // the base text of the item
    const ounces = item.mods.ounces ?? itemDef.ounces ?? 0;
    const encrustText = encrustDef ? ` set with ${encrustDef.desc}` : '';
    let desc = getProp(item, itemDef, 'desc');
    if (itemClass === interfaces_1.ItemClass.Coin) {
        desc = `${value.toLocaleString()} ${desc}`;
    }
    const ozText = itemClass !== interfaces_1.ItemClass.Bottle && ounces > 0 ? `${ounces} oz of ` : '';
    allTexts.push(`You are looking at ${ozText}${desc}${encrustText}.`);
    // display the number of upgrades an item has/available
    if (maxUpgrades > 0 || upgrades?.length > 0) {
        allTexts.push(`It has ${upgrades?.length ?? 0}/${maxUpgrades} magical slots filled.`);
        upgradeDefs.forEach((upg) => {
            allTexts.push(`It is slotted with ${upg.desc}.`);
        });
    }
    // whether the item has an implicit sell price or not
    if (itemDef.sellValue || thiefTier > 0) {
        if (thiefTier > 0) {
            allTexts.push(`The item is worth ${value.toLocaleString()} gold.`);
        }
        else {
            allTexts.push('It looks valuable.');
        }
    }
    if (identifyTier > 0 && extendedDesc) {
        allTexts.push(`This item is ${extendedDesc}.`);
    }
    if (identifyTier > 0 && (stats.offense || stats.defense)) {
        allTexts.push(`The combat adds are ${stats.offense || 0}/${stats.defense || 0}.`);
    }
    if (identifyTier > 1 &&
        itemClass !== interfaces_1.ItemClass.Bottle &&
        (useEffect || strikeEffect)) {
        let sense2Text = '';
        sense2Text = `This item has ${useEffect ? 'castable' : 'on-contact'} ${(useEffect || strikeEffect).name}`;
        if ((useEffect || strikeEffect).potency) {
            sense2Text = `${sense2Text} with a potency of ${(useEffect || strikeEffect).potency}.`;
        }
        else {
            sense2Text = `${sense2Text}.`;
        }
        allTexts.push(sense2Text);
    }
    if (identifyTier > 2 && tier > 0) {
        allTexts.push(`This item is tier ${tier}.`);
    }
    if (identifyTier > 0 && affectsAttributes) {
        allTexts.push('This item affects physical attributes!');
    }
    if (identifyTier > 2 && affectedStats.length > 0) {
        allTexts.push(`This item affects your stats! ${affectedStats
            .map((x) => `${formatStatForDisplay(x, stats[x] ?? 0)}`)
            .join(', ')}. `);
    }
    // whether it can be used in either hand
    if (isOffhand) {
        allTexts.push('The item is lightweight enough to use in either hand.');
    }
    // trait text
    if (identifyTier > 0 && trait?.restrict?.length > 0) {
        allTexts.push(`This rune can be learned by ${trait.restrict.join(' and ')}.`);
    }
    // the items 'use' effect situation
    if (usesText) {
        allTexts.push(`The item ${usesText}.`);
    }
    // how full it is, if at all
    if (itemClass === interfaces_1.ItemClass.Bottle && ounces > 0) {
        allTexts.push(`It is filled with ${ounces} oz of fluid.`);
    }
    if (itemClass === interfaces_1.ItemClass.Bottle && ounces === 0) {
        allTexts.push('It is empty.');
    }
    if (itemClass === interfaces_1.ItemClass.Bottle && ounces === -1) {
        allTexts.push('It replenishes itself infinitely.');
    }
    // various requirements for the item
    if (requirements && requirements.level) {
        allTexts.push(`You must be level ${requirements.level} to use this item.`);
    }
    if (requirements && requirements.skill) {
        const formattedSkill = requirements?.skill?.name === interfaces_1.Skill.Wand
            ? 'Magical Weapons'
            : requirements?.skill?.name;
        allTexts.push(`This item requires ${formattedSkill} skill ${requirements.skill.level}.`);
    }
    // book stuffs
    if (itemClass === interfaces_1.ItemClass.Book) {
        allTexts.push(`The book has ${pages?.length ?? 0} page(s) in it.`);
    }
    // trap stuffs
    if (itemClass === interfaces_1.ItemClass.TrapSet) {
        allTexts.push('This trap is live.');
    }
    // the crafter
    if (item.mods.craftedBy) {
        allTexts.push(`This item was made by ${item.mods.craftedBy}.`);
    }
    // the owner text
    if (item.mods.owner) {
        if (item.mods.owner === player.username) {
            allTexts.push('This item belongs to you.');
        }
        else {
            allTexts.push('This item does NOT belong to you.');
        }
    }
    allTexts.push(`The item is in ${conditionString(item)} condition.`);
    return allTexts.join(' ');
}
function foodTextFor(player, item, itemDef) {
    const desc = getProp(item, itemDef, 'desc');
    const useEffect = getProp(item, itemDef, 'useEffect');
    if (!useEffect)
        return '';
    const baseText = `You are looking at ${desc}.`;
    const statText = `This food changes the following stats: ${useEffect.extra?.tooltip ?? 'None'}`;
    return `${baseText} ${statText}`;
}
function gemTextFor(player, item, itemDef) {
    const desc = getProp(item, itemDef, 'desc');
    const encrustGive = getProp(item, itemDef, 'encrustGive');
    if (!encrustGive)
        return '';
    const affectedStats = Object.keys(encrustGive.stats || {});
    const baseText = `You are looking at ${desc}. `;
    const slotText = `This gem goes in the following slots: ${encrustGive.slots
        .map((x) => x.toUpperCase())
        .join(', ')}. `;
    const affectedStatsText = affectedStats
        .map((x) => `${formatStatForDisplay(x, encrustGive.stats[x])}`)
        .join(', ');
    const statText = affectedStats.length > 0
        ? `This gem changes the following stats: ${affectedStatsText}. `
        : '';
    const strikeEffect = encrustGive.strikeEffect;
    const effectText = strikeEffect
        ? `This gem confers the on-hit effect ${strikeEffect.name} at potency ${strikeEffect.potency}. `
        : '';
    return `${baseText}${slotText}${statText}${effectText}`;
}
function trinketExpMax(trinket, trinketDef) {
    const levelup = trinketDef?.levelup;
    if (!levelup)
        return 0;
    const currentLevel = trinket.mods.levelupCurrent?.currentLevel ?? 0;
    return levelup.xpPerLevel * (currentLevel + 1) * levelup.xpScalarPerLevel;
}
function trinketGoldCost(trinket, trinketDef) {
    const levelup = trinketDef?.levelup;
    if (!levelup)
        return 0;
    const currentLevel = trinket.mods.levelupCurrent?.currentLevel ?? 0;
    return levelup.goldCostPerLevel * (currentLevel + 1);
}
function trinketCanLevelUp(trinket, trinketDef) {
    const levelup = trinketDef?.levelup;
    if (!levelup)
        return false;
    const currentLevel = trinket.mods.levelupCurrent?.currentLevel ?? 0;
    const currentXp = trinket.mods.levelupCurrent?.currentXp ?? 0;
    return (currentXp >= trinketExpMax(trinket, trinketDef) &&
        currentLevel < levelup.maxLevel);
}
